/**
 * The entity an analytics column's routes and cached reads are addressed by: a
 * control, or a work step.
 *
 * The procedure payload carries no work-step id, so the owner is supplied by
 * the page or session hosting the procedure rather than read off it.
 *
 * This module is the only place the route base and the cache-key prefix are
 * spelled. A lint rule refuses the control spellings anywhere else, because a
 * copy that still says `"control"` typechecks and leaves a work step's reads
 * stale with nothing on screen saying so.
 */
export type AnalyticsOwnerKind = "control" | "workStep";

export interface AnalyticsOwner {
  kind: AnalyticsOwnerKind;
  id: string;
}

/** A control owner, or none when there is no id to address it by. */
export function controlAnalyticsOwner(
  controlId: string | null | undefined,
): AnalyticsOwner | undefined {
  return controlId ? { kind: "control", id: controlId } : undefined;
}

/** A work-step owner, or none when there is no id to address it by. */
export function workStepAnalyticsOwner(
  workStepId: string | null | undefined,
): AnalyticsOwner | undefined {
  return workStepId ? { kind: "workStep", id: workStepId } : undefined;
}

/** The owner as the dialogs name it to the user. */
export function analyticsOwnerNoun(kind: AnalyticsOwnerKind): string {
  switch (kind) {
    case "control":
      return "control";
    case "workStep":
      return "work step";
  }
}

export function analyticsPath(owner: AnalyticsOwner): string {
  switch (owner.kind) {
    case "control":
      return `/controls/v2/control/${owner.id}/analytics`;
    case "workStep":
      return `/work-steps/work-step/${owner.id}/analytics`;
  }
}

/**
 * The prefix every analytics read is cached under and every analytics write
 * invalidates. The control spelling is the one keys have always used.
 */
export function analyticsKeyPrefix(
  owner: AnalyticsOwner,
): [AnalyticsOwnerKind, string, "analytics"] {
  return [owner.kind, owner.id, "analytics"];
}
